/**
 * The account page's "My books": for each world, the reader's books by
 * status, what they own, how far they have got in each series, and their
 * latest quiz result. Read-only here; a book is marked from its own page.
 *
 * One world today, but nothing names it: the worlds come from the API, and a
 * world where the reader has nothing and no quiz result shows nothing.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { worldPath } from '../../shared/routes.ts'
import type { ShelfResponse, ShelfStatus } from '../../shared/shelf.ts'
import { useI18n } from '../i18n/index.tsx'
import { fill, mainTitle, seriesName, useWorldCatalogue, useWorlds, workBySlug, worldName, type Loaded } from '../lib/catalogue.ts'
import { fetchShelf } from '../lib/shelf.tsx'
import { Path, type PathStop } from './Path.tsx'

export function AccountShelves() {
  const { t } = useI18n()
  const worlds = useWorlds()
  if (worlds.status !== 'ready') return null
  return (
    <section className="account-shelves" aria-labelledby="account-shelves-heading">
      <h2 id="account-shelves-heading">{t('account.shelf.title')}</h2>
      {worlds.worlds.map((world) => (
        <WorldShelf key={world.slug} slug={world.slug} />
      ))}
    </section>
  )
}

function WorldShelf({ slug }: { slug: string }) {
  const { t, locale } = useI18n()
  const catalogue = useWorldCatalogue(slug)
  const [shelf, setShelf] = useState<ShelfResponse | null | 'loading'>('loading')

  useEffect(() => {
    let live = true
    fetchShelf(slug).then(
      (data) => {
        if (live) setShelf(data)
      },
      () => {
        if (live) setShelf(null)
      },
    )
    return () => {
      live = false
    }
  }, [slug])

  if (catalogue.status !== 'loaded' || shelf === 'loading' || shelf === null) return null
  if (shelf.entries.length === 0 && shelf.quiz === null) return <p>{t('account.shelf.empty')}</p>

  return (
    <div className="account-world">
      <h3>{worldName(catalogue.universe, locale)}</h3>
      {(['reading', 'want', 'read'] as ShelfStatus[]).map((status) => (
        <BookList
          key={status}
          title={t(`account.shelf.${status}`)}
          works={shelf.entries.filter((entry) => entry.status === status).map((entry) => entry.work)}
          catalogue={catalogue}
          world={slug}
        />
      ))}
      <BookList
        title={t('account.shelf.owned')}
        works={shelf.entries.filter((entry) => entry.owned).map((entry) => entry.work)}
        catalogue={catalogue}
        world={slug}
      />
      <Progress shelf={shelf} catalogue={catalogue} world={slug} />
      <QuizResult shelf={shelf} catalogue={catalogue} world={slug} />
    </div>
  )
}

/**
 * A group of the reader's books as cards: each the book's title, linking to
 * it, and where it stands in its series. The whole card is the link, as the
 * series cards on a world's page are.
 */
function BookList({ title, works, catalogue, world }: { title: string; works: string[]; catalogue: Loaded; world: string }) {
  const { t, locale } = useI18n()
  if (works.length === 0) return null
  return (
    <section className="account-group">
      <h4 className="label">{title}</h4>
      <ul className="account-cards">
        {works.flatMap((slug) => {
          const work = workBySlug(catalogue, slug)
          if (work === undefined) return []
          const place = catalogue.series.flatMap((series) => {
            const index = series.books.findIndex((book) => book.work === slug)
            return index === -1 ? [] : [{ series, index }]
          })[0]
          return [
            <li key={slug} className="series-card account-card">
              <h5 className="series-card__title">
                <Link to={worldPath(world, 'books', slug)}>{mainTitle(work, locale)}</Link>
              </h5>
              {place === undefined ? null : (
                <p className="label">
                  {seriesName(place.series, locale)} · {fill(t('book.position'), { position: place.index + 1, count: place.series.books.length })}
                </p>
              )}
            </li>,
          ]
        })}
      </ul>
    </section>
  )
}

/**
 * Each series with at least one book read, drawn as its path: the numbered
 * medallions and the dotted brass line of the series pages, with the books
 * read filled. The reward for registering, so it looks like the rest of the
 * site rather than a list.
 */
function Progress({ shelf, catalogue, world }: { shelf: ShelfResponse; catalogue: Loaded; world: string }) {
  const { t, locale } = useI18n()
  const read = new Set(shelf.entries.filter((entry) => entry.status === 'read').map((entry) => entry.work))
  const rows = catalogue.series
    .map((series) => ({ series, read: series.books.filter((book) => read.has(book.work)).length }))
    .filter((row) => row.read > 0)
  if (rows.length === 0) return null
  return (
    <section className="account-group">
      <h4 className="label">{t('account.shelf.progress')}</h4>
      <div className="account-paths">
        {rows.map(({ series, read: count }) => {
          const stops: PathStop[] = series.books.flatMap((book, index) => {
            const work = workBySlug(catalogue, book.work)
            return work === undefined
              ? []
              : [{ position: index + 1, title: mainTitle(work, locale), href: worldPath(world, 'books', work.slug), year: null, done: read.has(work.slug) }]
          })
          return (
            <section key={series.slug} className="path-panel account-path">
              <h5>
                <Link to={worldPath(world, 'series', series.slug)}>{seriesName(series, locale)}</Link>
              </h5>
              <p className="account-path__count">{fill(t('series.progress'), { read: count, count: series.books.length })}</p>
              <Path stops={stops} label={seriesName(series, locale)} compact doneLabel={t('account.shelf.read_mark')} />
            </section>
          )
        })}
      </div>
    </section>
  )
}

function QuizResult({ shelf, catalogue, world }: { shelf: ShelfResponse; catalogue: Loaded; world: string }) {
  const { t, locale } = useI18n()
  const quiz = shelf.quiz
  if (quiz === null) return null
  const name = (slug: string) => {
    const series = catalogue.series.find((s) => s.slug === slug)
    return series === undefined ? slug : seriesName(series, locale)
  }
  const date = new Intl.DateTimeFormat(locale === 'bg' ? 'bg-BG' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Sofia',
  }).format(new Date(quiz.taken_at))
  return (
    <section className="account-group">
      <h4 className="label">{t('account.quiz.title')}</h4>
      <div className="series-card account-quiz">
        <p>{fill(t('account.quiz.result'), { first: name(quiz.first), second: name(quiz.second) })}</p>
        <p className="account-quiz__links">
          <Link to={`${worldPath(world, 'quiz')}?a=${encodeURIComponent(quiz.answers)}`}>{fill(t('account.quiz.taken'), { date })}</Link>
          <Link to={worldPath(world, 'quiz')}>{t('account.quiz.again')}</Link>
        </p>
      </div>
    </section>
  )
}
